import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import {
  createMenu,
  fetchMenuCategories,
  fetchMenus,
  resolveImageUrl,
  setMenuSoldOut,
} from '../../api/appmanageMenus';
import { Menu, MenuCategory } from '../../types/admin';
import './MenuManagementScreen.scss';

const NEW_CATEGORY = 'new';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function MenuCreateForm({ onCreated }: { onCreated: () => void }) {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCategories = async () => {
    try {
      const data = await fetchMenuCategories();
      if (!Array.isArray(data)) throw new Error('카테고리 목록을 불러오지 못했습니다.');
      setCategories(data);
      setCategoryId((prev) => prev || (data[0] ? String(data[0].id) : NEW_CATEGORY));
    } catch (err) {
      setError(err instanceof Error ? err.message : '카테고리 목록을 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (!image) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const isNewCategory = categoryId === NEW_CATEGORY;

  const clearImage = () => {
    setImage(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > MAX_IMAGE_BYTES) {
      setError('이미지는 5MB 이하만 업로드할 수 있습니다.');
      clearImage();
      return;
    }
    setError(null);
    setImage(file);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createMenu({
        ...(isNewCategory ? { newCategoryName } : { categoryId: Number(categoryId) }),
        name,
        price: Number(price),
        image,
      });
      setName('');
      setPrice('');
      clearImage();
      if (isNewCategory) {
        setNewCategoryName('');
        const data = await fetchMenuCategories();
        setCategories(data);
        const created = data.find((c) => c.name === newCategoryName.trim());
        if (created) setCategoryId(String(created.id));
      }
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.message || '메뉴 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="MenuCreateForm" onSubmit={handleSubmit}>
      <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
        <option value={NEW_CATEGORY}>+ 새 카테고리</option>
      </select>
      {isNewCategory && (
        <input
          type="text"
          placeholder="새 카테고리 이름"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          required
        />
      )}
      <input type="text" placeholder="메뉴 이름" value={name} onChange={(e) => setName(e.target.value)} required />
      <input
        type="number"
        placeholder="가격(원)"
        min={0}
        step={100}
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        required
      />
      <label className="MenuCreateForm-image">
        {imagePreview ? <img src={imagePreview} alt="메뉴 사진 미리보기" /> : <span>+ 사진</span>}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleImageChange}
        />
      </label>
      {image && (
        <button type="button" className="MenuCreateForm-image-remove" onClick={clearImage}>
          사진 삭제
        </button>
      )}
      <button type="submit" disabled={submitting}>
        {submitting ? '등록 중...' : '등록'}
      </button>
      {error && <p className="MenuCreateForm-error">{error}</p>}
    </form>
  );
}

function MenuList({ menus, setMenus }: { menus: Menu[]; setMenus: Dispatch<SetStateAction<Menu[]>> }) {

  const handleToggle = async (menu: Menu) => {
    const nextSoldOut = !menu.is_sold_out;
    setMenus((prev) => prev.map((m) => (m.id === menu.id ? { ...m, is_sold_out: nextSoldOut ? 1 : 0 } : m)));
    await setMenuSoldOut(menu.id, nextSoldOut);
  };

  if (!menus.length) return <p>등록된 메뉴가 없습니다.</p>;

  const menusByCategory = menus.reduce<Record<string, Menu[]>>((acc, menu) => {
    (acc[menu.category_name] ||= []).push(menu);
    return acc;
  }, {});

  return (
    <div className="MenuList">
      {Object.entries(menusByCategory).map(([category, categoryMenus]) => (
        <div key={category} className="MenuList-category">
          <h3>{category}</h3>
          <div className="MenuList-items">
            {categoryMenus.map((menu) => (
              <div key={menu.id} className="MenuList-item">
                <div className="MenuList-thumb">
                  {menu.image_url ? <img src={resolveImageUrl(menu.image_url)!} alt={menu.name} /> : null}
                </div>
                <div className="MenuList-info">
                  <p className="MenuList-name">{menu.name}</p>
                  <p className="MenuList-price">{menu.price.toLocaleString()}원</p>
                </div>
                <button
                  type="button"
                  className={menu.is_sold_out ? 'sold-out' : 'available'}
                  onClick={() => handleToggle(menu)}
                >
                  {menu.is_sold_out ? '품절' : '판매중'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MenuManagementScreen() {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMenus = async () => {
    const data = await fetchMenus();
    setMenus(data);
    setLoading(false);
  };

  useEffect(() => {
    loadMenus();
  }, []);

  return (
    <div className="MenuManagementScreen">
      <h1>메뉴 관리</h1>
      <section>
        <h2>메뉴 등록</h2>
        <MenuCreateForm onCreated={loadMenus} />
      </section>
      <section>
        <h2>메뉴 품절 관리</h2>
        {loading ? <p>불러오는 중...</p> : <MenuList menus={menus} setMenus={setMenus} />}
      </section>
    </div>
  );
}

export default MenuManagementScreen;
